/**
 * RightPanelBatch — Batch workspace for multi-selected documents.
 *
 * Layout:
 *  1. Header — "BATCH WORKSPACE" + count badge (brand background)
 *  2. Empty state — when 0 selected
 *  3. Batch Breakdown — document types with counts
 *  4. Export Method — Direct Sync (if offered) + CSV; CSV only for CSV-mode clients;
 *     CSV + upgrade upsell when there is no bridge at all
 *  5. Actions — Export + Delete
 *  6. ERP Sync Upgrade Modal (only when no bridge)
 */
import { useState } from "react";
import { ArrowNarrowRight, Check, CursorClick01, Download01, File06, Lightning01, Trash01, XClose } from "@untitledui/icons";
import { Dialog, Modal, ModalOverlay } from "@/components/application/modals/modal";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icons";
import { isDocumentExportable } from "@/components/home/helpers";
import type { HomeDocument } from "@/types/home";
import { cx } from "@/utils/cx";

const ERP_INTEGRATIONS = [
    "Sage 100",
    "Sage 300",
    "Microsoft Dynamics GP",
    "Microsoft Dynamics 365 Business Central",
    "QuickBooks Enterprise",
    "SAP Business One",
];

interface RightPanelBatchProps {
    /** Selected documents */
    selectedDocs: HomeDocument[];
    /** Number of selected documents */
    selectedCount: number;
    /** Current export format */
    exportFormat: string;
    /** Whether the client has a bridge configured (drives the upgrade upsell when absent) */
    hasBridge: boolean;
    /** Whether Direct Sync export is offered (bridge exists AND export mode is DirectSync) */
    canDirectSync: boolean;
    /** Bridge name (null if no bridge) */
    bridgeName: string | null;
    /** Called when export format changes */
    onExportFormatChange: (format: string) => void;
    /** Called when user exports selected documents */
    onExport: () => void;
    /** Called when user deletes selected documents */
    onDelete: () => void;
}

export function RightPanelBatch({
    selectedDocs,
    selectedCount,
    exportFormat,
    hasBridge,
    canDirectSync,
    bridgeName,
    onExportFormatChange,
    onExport,
    onDelete,
}: RightPanelBatchProps) {
    const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);

    const canExport = selectedCount > 0 && selectedDocs.every(isDocumentExportable);

    // Group by document type for breakdown
    const typeBreakdown = selectedDocs.reduce<Record<string, number>>((acc, doc) => {
        acc[doc.documentType] = (acc[doc.documentType] ?? 0) + 1;
        return acc;
    }, {});

    return (
        <div className="flex h-full flex-col overflow-hidden">
            {/* ---- Header (brand background) ---- */}
            <div className="shrink-0 border-b border-tertiary bg-brand-primary px-5 py-4">
                <div className="flex items-center justify-between">
                    <h2 className="text-xs font-semibold tracking-wider text-brand-secondary uppercase">Batch Workspace</h2>
                    <Badge size="sm" color="brand">
                        {selectedCount} items
                    </Badge>
                </div>
            </div>

            {/* ---- Scrollable content ---- */}
            <div className="custom-scrollbar flex-1 space-y-8 overflow-y-auto px-5 py-6">
                {selectedCount === 0 ? (
                    /* Empty state */
                    <div className="flex h-full flex-col items-center justify-center px-4 text-center">
                        <FeaturedIcon icon={CursorClick01} color="gray" theme="light" size="xl" />
                        <p className="mt-4 text-sm font-medium text-tertiary">No items selected</p>
                        <p className="mt-1 text-xs text-quaternary">Select documents from the list to begin batch actions</p>
                    </div>
                ) : (
                    <>
                        {/* Batch Breakdown */}
                        <section>
                            <h3 className="mb-4 text-xs font-semibold tracking-wider text-quaternary uppercase">Batch Breakdown</h3>
                            <div className="space-y-3">
                                {Object.entries(typeBreakdown).map(([type, typeCount]) => (
                                    <div key={type} className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <span className="size-2 shrink-0 rounded-full bg-fg-brand-primary" />
                                            <span className="text-sm font-semibold text-secondary">{type}s</span>
                                        </div>
                                        <Badge color="gray" size="sm">
                                            {typeCount}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Export Method */}
                        <section className="border-t border-tertiary pt-6">
                            <h3 className="mb-4 text-xs font-semibold tracking-wider text-quaternary uppercase">Export Method</h3>
                            <div className="space-y-2.5">
                                {canDirectSync ? (
                                    <>
                                        {/* Direct Sync — selectable option (bridge available, DirectSync mode) */}
                                        <button
                                            type="button"
                                            onClick={() => onExportFormatChange("external")}
                                            className={cx(
                                                "flex w-full cursor-pointer items-center justify-between rounded-xl border p-4 transition duration-100 ease-linear",
                                                exportFormat === "external"
                                                    ? "border-brand-solid bg-brand-primary ring-1 ring-brand"
                                                    : "border-secondary hover:border-primary",
                                            )}
                                        >
                                            <div className="flex items-center gap-3">
                                                <FeaturedIcon
                                                    icon={Lightning01}
                                                    color={exportFormat === "external" ? "brand" : "gray"}
                                                    theme="light"
                                                    size="sm"
                                                />
                                                <div className="text-left">
                                                    <span className="block text-sm font-semibold text-primary">Direct Sync</span>
                                                    <span className="text-xs text-tertiary">Push straight to {bridgeName}</span>
                                                </div>
                                            </div>
                                            {exportFormat === "external" && <Check className="size-4 text-fg-brand-primary" />}
                                        </button>

                                        {/* CSV Option */}
                                        <button
                                            type="button"
                                            onClick={() => onExportFormatChange("csv")}
                                            className={cx(
                                                "flex w-full cursor-pointer items-center justify-between rounded-xl border p-4 transition duration-100 ease-linear",
                                                exportFormat === "csv"
                                                    ? "border-brand-solid bg-brand-primary ring-1 ring-brand"
                                                    : "border-secondary hover:border-primary",
                                            )}
                                        >
                                            <div className="flex items-center gap-3">
                                                <FeaturedIcon icon={File06} color={exportFormat === "csv" ? "brand" : "gray"} theme="light" size="sm" />
                                                <span className="text-sm font-semibold text-primary">CSV Export</span>
                                            </div>
                                            {exportFormat === "csv" && <Check className="size-4 text-fg-brand-primary" />}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        {/* CSV Option */}
                                        <button
                                            type="button"
                                            onClick={() => onExportFormatChange("csv")}
                                            className={cx(
                                                "flex w-full cursor-pointer items-center justify-between rounded-xl border p-4 transition duration-100 ease-linear",
                                                exportFormat === "csv"
                                                    ? "border-brand-solid bg-brand-primary ring-1 ring-brand"
                                                    : "border-secondary hover:border-primary",
                                            )}
                                        >
                                            <div className="flex items-center gap-3">
                                                <FeaturedIcon icon={File06} color={exportFormat === "csv" ? "brand" : "gray"} theme="light" size="sm" />
                                                <span className="text-sm font-semibold text-primary">CSV Export</span>
                                            </div>
                                            {exportFormat === "csv" && <Check className="size-4 text-fg-brand-primary" />}
                                        </button>

                                        {/* Direct Sync — Upgrade upsell (no bridge only; CSV-mode
                                            clients HAVE a bridge for lookups, so no upsell either) */}
                                        {!hasBridge && (
                                            <div className="relative">
                                                <Badge color="warning" size="sm" className="absolute -top-2.5 -right-1.5 z-10 animate-pulse">
                                                    Upgrade
                                                </Badge>
                                                <button
                                                    type="button"
                                                    onClick={() => setIsSyncModalOpen(true)}
                                                    className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl border border-transparent bg-warning-primary p-4 transition-colors hover:bg-warning-secondary"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <FeaturedIcon icon={Lightning01} color="warning" theme="light" size="sm" />
                                                        <div className="text-left">
                                                            <span className="block text-sm font-semibold text-primary">Direct Sync</span>
                                                            <span className="text-xs text-tertiary">Push straight to your ERP</span>
                                                        </div>
                                                    </div>
                                                    <ArrowNarrowRight className="size-4 shrink-0 text-fg-warning-primary" />
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </section>
                    </>
                )}
            </div>

            {/* ---- Actions (pinned bottom) ---- */}
            <div className="shrink-0 space-y-2 border-t border-tertiary px-5 py-4">
                <Button size="md" color="primary" iconLeading={Download01} isDisabled={!canExport} className="w-full" onClick={onExport}>
                    Export
                </Button>
                {selectedCount > 0 && (
                    <Button size="md" color="secondary-destructive" className="w-full" iconLeading={Trash01} onClick={onDelete}>
                        Delete {selectedCount} items
                    </Button>
                )}
            </div>

            {/* ---- Direct Sync Upgrade Modal (only when no bridge) ---- */}
            {!hasBridge && (
                <ModalOverlay isOpen={isSyncModalOpen} onOpenChange={setIsSyncModalOpen} isDismissable>
                    <Modal className="max-w-md">
                        <Dialog>
                            <div className="w-full rounded-xl border border-secondary bg-primary shadow-xl">
                                {/* Header */}
                                <div className="flex items-center justify-between px-6 pt-6 pb-0">
                                    <div className="flex items-center gap-3">
                                        <FeaturedIcon icon={Lightning01} color="warning" theme="light" size="md" />
                                        <div>
                                            <h2 className="text-lg font-semibold text-primary">Direct ERP Sync</h2>
                                            <p className="text-sm text-tertiary">Premium integration feature</p>
                                        </div>
                                    </div>
                                    <Button color="tertiary" size="sm" iconLeading={XClose} onClick={() => setIsSyncModalOpen(false)} aria-label="Close" />
                                </div>

                                {/* Body */}
                                <div className="space-y-4 px-6 pt-5 pb-2">
                                    <p className="text-sm text-secondary">
                                        Skip the CSV export and sync your approved documents directly to your ERP system. AutoLedger integrates with major
                                        platforms including:
                                    </p>
                                    <div className="grid grid-cols-1 gap-2">
                                        {ERP_INTEGRATIONS.map((erp) => (
                                            <div key={erp} className="flex items-center gap-2.5 rounded-lg border border-secondary bg-secondary px-3 py-2.5">
                                                <Check className="size-4 shrink-0 text-fg-success-primary" />
                                                <span className="text-sm font-medium text-primary">{erp}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="border-warning rounded-lg border bg-warning-primary px-4 py-3">
                                        <p className="text-sm font-medium text-warning-primary">This feature requires the Professional plan or higher.</p>
                                        <p className="mt-1 text-xs text-tertiary">
                                            Upgrade your account to enable direct sync, automated matching, and real-time ERP posting.
                                        </p>
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="flex items-center justify-end gap-3 px-6 py-5">
                                    <Button color="secondary" size="md" onClick={() => setIsSyncModalOpen(false)}>
                                        Maybe Later
                                    </Button>
                                    <Button color="primary" size="md" iconTrailing={ArrowNarrowRight}>
                                        View Plans
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
}
